import React from "react";
import { motion } from "framer-motion";
import SEO from "../Seo/SEO";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";

export default function PrivacyPolicy() {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  return (
    <div style={{ paddingTop: "80px" }}>
      <Header />
      <div className="py-8">
      <SEO
        title="Privacy Policy - pujaguru"
        description="Read pujaguru's privacy policy. Learn how we collect, use, and protect your personal information when you use our online puja booking services."
        keywords="privacy policy, data protection, privacy, pujaguru privacy"
        url="https://staging.pujaguru.org/privacy_policy"
        canonical="https://staging.pujaguru.org/privacy_policy"
        noindex={false}
      />
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="md:text-3xl text-2xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h2>
          <p className="md:text-lg text-base text-gray-600 max-w-2xl mx-auto">
            Please read this privacy policy carefully before using our services.
            By accessing and using pujaguru, you accept and agree to be bound
            by these terms.
          </p>
          <p className="md:text-base text-sm text-gray-500 mt-2">
            Last Updated: 13-02-2026
          </p>
          <div className="w-24 h-1 bg-orange-500 mx-auto mt-6 rounded-full"></div>
        </motion.div>

        <div className="space-y-8">
          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                1.
              </span>
              Introduction
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>
                The mobile application and digital platform "pujaguru" ("Platform") is owned and operated by pujaguru (hereinafter referred to as "pujaguru", "We", "Us", or "Our"), which expression shall, unless repugnant to the context or meaning thereof, include its successors, affiliates, and permitted assigns.
              </p>
              <p>
                pujaguru is committed to protecting your privacy and ensuring the security of the information shared with us. This Privacy Policy ("Policy") explains how pujaguru collects, uses, stores, processes, and discloses information received from customers, devotees, users, astrologers, priests, and service partners while using the Platform.
              </p>
              <p>
                This Policy is framed in accordance with the Information Technology Act, 2000, the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, and the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021.
              </p>
              <p>
                By accessing, registering, or using the Platform, you acknowledge that you have read, understood, and agreed to the terms of this Privacy Policy.
              </p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 0.1 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                2.
              </span>
              Scope & Applicability
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>
                This Privacy Policy is an electronic record and constitutes a legally binding agreement under applicable Indian laws. No physical or digital signature is required.
              </p>
              <p>
                By using the pujaguru Platform, you confirm that you are legally competent to enter into a binding contract under the Indian Contract Act, 1872.
              </p>
              <p>
                This Policy applies to all users, customers, devotees, astrologers, priests, and service partners accessing the Platform and governs the collection and use of information only to the extent necessary for lawful purposes.
              </p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 0.2 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                3.
              </span>
              Consent
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>
                By using the Platform, you expressly consent to the collection, use, storage, sharing, and processing of your information in accordance with this Policy.
              </p>
              <p>
                If you do not agree with this Policy, please do not use the Platform.
              </p>
              <p>
                Certain services may require registration and login credentials. The use of such credentials shall be governed by this Privacy Policy along with the applicable Terms of Use.
              </p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 0.3 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                4.
              </span>
              Types of Information Collected
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p><strong>(a) Personal Information</strong></p>
              <p>Information that can identify an individual, including but not limited to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Name, mobile number, email address</li>
                <li>Date of birth, gender</li>
                <li>Address and location details</li>
                <li>Date, time, and place of birth (for astrology services)</li>
                <li>Government-issued identification (where required)</li>
                <li>Payment and banking details</li>
              </ul>

              <p><strong>(b) Sensitive Personal Data (SPDI)</strong></p>
              <p>Includes passwords, financial information, and other sensitive data as defined under applicable law.</p>

              <p><strong>(c) Non-Personal Information</strong></p>
              <p>Information that does not identify an individual, such as:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device information</li>
                <li>Operating system</li>
                <li>App usage patterns</li>
              </ul>

              <p><strong>(d) Usage Information</strong></p>
              <p>Automatically collected data including app activity, session duration, navigation patterns, and interaction logs.</p>

              <p>All the above are collectively referred to as "Information."</p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 0.4 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                5.
              </span>
              Collection of Information
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>pujaguru may collect Information when you:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Register or update your account</li>
                <li>Book puja services, chadhava seva, or consultations</li>
                <li>Make payments</li>
                <li>Participate in surveys, feedback, or promotions</li>
                <li>Contact customer support</li>
              </ul>
              <p>Information is provided voluntarily and without coercion.</p>
              <p>Cookies and similar technologies may be used to enhance user experience and platform functionality.</p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 0.5 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                6.
              </span>
              Use of Information
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>pujaguru uses collected Information to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide and manage services</li>
                <li>Process bookings and payments</li>
                <li>Communicate service updates and confirmations</li>
                <li>Improve platform performance and user experience</li>
                <li>Send promotional and informational communications</li>
                <li>Resolve disputes and technical issues</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 0.6 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                7.
              </span>
              Information Sharing & Disclosure
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>pujaguru does not sell or rent your Personal Information.</p>
              <p>Information may be shared only:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>With priests, astrologers, or service partners for service fulfillment</li>
                <li>With payment gateway providers for transaction processing</li>
                <li>With third-party vendors under strict confidentiality</li>
                <li>With government authorities when required by law</li>
              </ul>
              <p>Aggregated or anonymized data may be used for analytics and business insights.</p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 0.7 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                8.
              </span>
              Third-Party Services
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>
                The Platform may contain links to third-party websites or services. pujaguru is not responsible for the privacy practices or content of such third parties. Users are advised to review their respective privacy policies.
              </p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 0.8 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                9.
              </span>
              Data Retention
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>
                Personal Information is retained only as long as required by law or for the purpose for which it was collected. Non-personal data may be retained longer for analytics and improvement purposes.
              </p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 0.9 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                10.
              </span>
              User Rights & Control
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>You may:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access or update your information</li>
                <li>Withdraw consent</li>
                <li>Request account deletion</li>
              </ul>
              <p className="flex items-center gap-1">
                Requests can be sent to: 📧<br />
                <a className="text-orange-600" href="mailto:support@pujaguru.org">
                  support@pujaguru.org
                </a>
              </p>
              <p>Certain services may be restricted upon withdrawal of consent.</p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 1.0 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                11.
              </span>
              Security Measures
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>
                pujaguru uses industry-standard technical, administrative, and physical safeguards to protect Information. However, absolute security over the internet cannot be guaranteed.
              </p>
              <p>
                Users are responsible for maintaining the confidentiality of login credentials.
              </p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 1.1 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                12.
              </span>
              Minors
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>
                The Platform is not intended for use by minors without parental consent. If services are used on behalf of a minor, the guardian assumes responsibility.
              </p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 1.2 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                13.
              </span>
              Limitation of Liability
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>
                pujaguru shall not be liable for indirect, incidental, or consequential damages arising from use of the Platform or third-party services, including force majeure events beyond reasonable control.
              </p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 1.3 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                14.
              </span>
              Opt-Out of Communications
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>
                Users may opt out of promotional communications but will continue to receive transactional and service-related messages.
              </p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 1.4 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                15.
              </span>
              Policy Updates
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>
                pujaguru reserves the right to modify this Policy at any time. Continued use of the Platform implies acceptance of the updated Policy.
              </p>
            </div>
          </motion.section>

          <motion.section
            className="bg-white rounded-xl shadow md:p-5  p-3"
            {...fadeInUp}
            transition={{ delay: 1.5 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                16.
              </span>
              Governing Law & Jurisdiction
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>
                This Policy shall be governed by the laws of India. Courts in India shall have exclusive jurisdiction. Any disputes shall be resolved through arbitration under the Arbitration and Conciliation Act, 1996.
              </p>
            </div>
          </motion.section>
          <motion.section
            className="bg-white rounded-xl shadow md:p-5 p-3"
            {...fadeInUp}
            transition={{ delay: 1.6 }}
          >
            <h4 className="md:text-2xl text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                17.
              </span>
              Grievance Redressal
            </h4>
            <div className="text-gray-700 space-y-4 md:text-base text-sm">
              <p>For privacy-related concerns or complaints:</p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p>
                  📧 <strong>Email:</strong>{" "}
                  <a
                    href="mailto:support@pujaguru.org"
                    className="text-orange-600 hover:underline"
                  >
                    support@pujaguru.org
                  </a>
                </p>

                <p>
                  📞 <strong>Phone:</strong>{" "}
                  <a
                    href="tel:7201031069"
                    className="text-orange-600 hover:underline"
                  >
                    7201031069
                  </a>
                </p>
              </div>

              <p>We will address grievances in accordance with applicable Indian laws.</p>
            </div>
          </motion.section>
        </div>

        <motion.div
          className="text-center mt-12 md:p-6 p-3 bg-orange-50 rounded-xl border border-orange-200"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 0.8 }}
        >
          <p className="text-gray-700 md:text-base text-sm">
            <strong>Important:</strong> By using pujaguru services, you
            acknowledge that you have read, understood, and agree to be bound by
            this Privacy Policy. If you do not agree with any part of this
            policy, please do not use our services.
          </p>
        </motion.div>
      </div>
      </div>
      <Footer />
    </div>
  );
}
